import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Info, Loader2, AlertCircle } from 'lucide-react';
import ProductInfoModal from '@/components/ProductInfoModal';
import { useBundleSelection } from '@/hooks/useBundleSelection';
import type { Kit, KitSelection } from '@/types/kit';
import type { Equipo, Mascarilla } from '@/context/AppContext';

export interface KitSelectionCardProps {
  kit: Kit;
  isSelected?: boolean;
  onSelect: (kitSelection: KitSelection) => void;
  maskRecommendation?: string;
  maskSize?: string;
  className?: string;
}

const KitSelectionCard: React.FC<KitSelectionCardProps> = ({
  kit,
  isSelected = false,
  onSelect,
  maskRecommendation,
  maskSize,
  className = ""
}) => {
  const [showProductModal, setShowProductModal] = useState<boolean>(false);
  const [selectedProduct, setSelectedProduct] = useState<Equipo | Mascarilla | null>(null);
  const [productModalType, setProductModalType] = useState<'equipo' | 'mascarilla'>('equipo');

  // Find the mask and CPAP components from the kit
  const equipoComponent = kit.components.find(c => c.type === 'equipo');
  const mascarillaComponent = kit.components.find(c => c.type === 'mascarilla');

  // Convert kit components to individual product format for ProductInfoModal
  const equipoForModal: Equipo | null = equipoComponent ? {
    marca: equipoComponent.marca,
    modelo: equipoComponent.modelo,
    precio: 0, // Pricing now comes from Shopify bundle
    conectividad: equipoComponent.conectividad || 'Bluetooth',
    garantia: equipoComponent.garantia || '1 año',
    caracteristicas: equipoComponent.caracteristicas,
    imagen: equipoComponent.imagen
  } : null;

  const mascarillaForModal: Mascarilla | null = mascarillaComponent ? {
    marca: mascarillaComponent.marca,
    modelo: mascarillaComponent.modelo,
    precio: 0, // Pricing now comes from Shopify bundle
    tipo: mascarillaComponent.tipo || 'Nasal',
    caracteristicas: mascarillaComponent.caracteristicas,
    compatibilidad: mascarillaComponent.compatibilidad || [],
    imagen: mascarillaComponent.imagen
  } : null;

  // Use bundle selection hook to get real pricing
  const { bundle: shopifyBundle, fallbackBundle, isLoading, error } = useBundleSelection(kit);

  // Determine final price - use Shopify bundle if available, otherwise fallback
  const finalPrice = shopifyBundle?.price || fallbackBundle?.price || 0;

  const handleCardClick = () => {
    if (isLoading) return;
    
    const kitSelection: KitSelection = {
      kit,
      selectedVariantId: shopifyBundle?.variants?.[0]?.id,
      customizations: {},
      precioFinal: finalPrice,
      selectedAt: new Date(),
      selectionMethod: maskRecommendation === kit.tipo ? 'recommendation' : 'manual'
    };
    
    onSelect(kitSelection);
  };

  const openProductInfo = (product: Equipo | Mascarilla, type: 'equipo' | 'mascarilla', e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedProduct(product);
    setProductModalType(type);
    setShowProductModal(true);
  };

  const closeProductModal = () => {
    setShowProductModal(false);
    setSelectedProduct(null);
  };

  const isRecommended = maskRecommendation === kit.tipo;

  return (
    <>
      <Card 
        className={`cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-[1.01] border-2 overflow-hidden ${
          isSelected 
            ? 'border-primary shadow-md ring-2 ring-primary/20' 
            : 'border-border hover:border-primary/50'
        } ${className}`}
        style={{ boxSizing: 'border-box' }}
        onClick={handleCardClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleCardClick();
          }
        }}
        aria-label={`Seleccionar kit ${kit.name}`}
        aria-pressed={isSelected}
      >
        <CardContent className="p-4 sm:p-6 relative">
          {/* Selection indicator */}
          <div className="absolute top-4 right-4">
            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
              isSelected 
                ? 'bg-primary border-primary' 
                : 'border-muted-foreground'
            }`}>
              {isSelected && (
                <CheckCircle className="h-4 w-4 text-primary-foreground" />
              )}
            </div>
          </div>

          {/* Kit header */}
          <div className="flex items-start gap-3 mb-4">
            <div className="w-20 h-20 sm:w-24 sm:h-24 flex-shrink-0 bg-white rounded-lg overflow-hidden border border-border/10">
              <img 
                src={kit.imagen} 
                alt={kit.name}
                className="w-full h-full object-contain"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.parentElement!.innerHTML = '<div class="w-full h-full bg-muted/20 flex items-center justify-center"><svg class="h-6 w-6 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path></svg></div>';
                }}
              />
            </div>
            <div className="flex-1 min-w-0 pr-10">
              <div className="mb-2">
                <h3 className="text-lg sm:text-xl font-bold text-primary mb-1 overflow-hidden" 
                    style={{display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical'}}
                >
                  {kit.name}
                </h3>
                <p className="text-sm text-muted-foreground">
                  <span className="inline sm:hidden">{kit.marca}</span>
                  <span className="hidden sm:inline">{kit.marca} • {kit.tipo}</span>
                  <br className="sm:hidden" />
                  <span className="inline sm:hidden">{kit.tipo}</span>
                </p>
              </div>
              
              {/* Pricing section */}
              <div className="flex items-center gap-2">
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm text-muted-foreground">Cargando precio...</span>
                  </div>
                ) : error ? (
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-amber-600" />
                    <span className="text-lg font-bold text-accent">
                      ${finalPrice.toLocaleString()}
                    </span>
                  </div>
                ) : (
                  <>
                    <span className="text-lg sm:text-xl font-bold text-accent">
                      ${finalPrice.toLocaleString()}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Kit description */}
          <p className="text-sm text-muted-foreground mb-4">{kit.descripcion}</p>

          {/* Kit components */}
          <div className="space-y-3">
            <p className="text-sm font-semibold text-muted-foreground">Incluye:</p>
            
            {/* CPAP Equipment */}
            {equipoComponent && equipoForModal && (
              <Button
                variant="ghost"
                size="sm"
                className="flex items-center justify-between p-3 py-12 w-full bg-background/50 rounded-lg border border-border/30"
                onClick={(e) => openProductInfo(equipoForModal, 'equipo', e)}
                aria-label={`Más información sobre ${equipoComponent.modelo}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 flex-shrink-0 bg-white rounded-lg overflow-hidden border border-border/10">
                    <img 
                      src={equipoComponent.imagen} 
                      alt={equipoComponent.modelo}
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.parentElement!.innerHTML = '<div class="w-full h-full bg-muted/20 flex items-center justify-center"><svg class="h-4 w-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path></svg></div>';
                      }}
                    />
                  </div>
                  <div>
                    <h4 className="font-medium text-sm text-foreground text-start">{equipoComponent.modelo}</h4>
                    <p className="text-xs text-muted-foreground text-start" style={{lineHeight: "1.2rem"}}>
                      <span className="inline sm:hidden">{equipoComponent.marca}</span>
                      <span className="hidden sm:inline">{equipoComponent.marca} • Equipo CPAP Automático</span>
                      <br className="sm:hidden" />
                      <span className="inline sm:hidden">Equipo CPAP Automático</span>
                    </p>
                  </div>
                </div>
                <div className='flex items-center'>
                  <Info className="h-3 w-3 mr-2 sm:mr-0 lg:mr-2" />
                  <span className="ml-1 hidden sm:inline lg:hidden mr-4">Más información</span>
                </div>
                
              </Button>
            )}

            {/* Mask */}
            {mascarillaComponent && mascarillaForModal && (
              <Button
                variant="ghost"
                size="sm"
                className="flex items-center justify-between p-3 py-12 w-full bg-background/50 rounded-lg border border-border/30"
                onClick={(e) => openProductInfo(mascarillaForModal, 'mascarilla', e)}
                aria-label={`Más información sobre ${mascarillaComponent.modelo}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 flex-shrink-0 bg-white rounded-lg overflow-hidden border border-border/10">
                    <img 
                      src={mascarillaComponent.imagen} 
                      alt={mascarillaComponent.modelo}
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.parentElement!.innerHTML = '<div class="w-full h-full bg-muted/20 flex items-center justify-center"><svg class="h-4 w-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path></svg></div>';
                      }}
                    />
                  </div>
                  <div>
                    <h4 className="font-medium text-sm text-foreground text-start">{mascarillaComponent.modelo}</h4>
                    <p className="text-xs text-muted-foreground text-start" style={{lineHeight: "1.2rem"}}>
                      <span className="inline sm:hidden">{mascarillaComponent.marca}</span>
                      <span className="hidden sm:inline">{mascarillaComponent.marca} • Mascarilla {mascarillaComponent.tipo || 'Nasal'} • Talla {maskSize || 'xx'}</span>
                      <br className="sm:hidden" />
                      <span className="inline sm:hidden">Mascarilla {mascarillaComponent.tipo || 'Nasal'}</span>
                      <br className="sm:hidden" />
                      <span className="inline sm:hidden">Talla {maskSize || 'xx'}</span>
                    </p>
                  </div>
                </div>
                <div className='flex items-center'>
                  <Info className="h-3 w-3 mr-2 sm:mr-0 lg:mr-2" />
                  <span className="ml-1 hidden sm:inline lg:hidden mr-4">Más información</span>
                </div>
              </Button>
            )}
          </div>

          {/* Kit benefits */}
          {kit.beneficios && kit.beneficios.length > 0 && (
            <div className="mt-4 pt-3 border-t border-border">
              <ul className="text-sm text-muted-foreground space-y-1">
                {kit.beneficios.slice(0, 3).map((benefit, index) => (
                  <li key={index}>• {benefit}</li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Product Information Modal */}
      <ProductInfoModal 
        isOpen={showProductModal} 
        onClose={closeProductModal} 
        product={selectedProduct} 
        type={productModalType} 
      />
    </>
  );
};

export default KitSelectionCard;